import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["bypassTrackingRef", "children", "hoverThreshold", "onInterest", "onNegativeFeedback", "viewThreshold"];
import { useRef, useEffect, useCallback } from 'react';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
// Minimum percentage of component that must be visible to start tracking
const VISIBILITY_THRESHOLD = 0.9; // 90%

export function InterestFeedbackTracker(_ref) {
  let {
      bypassTrackingRef,
      children,
      hoverThreshold = 5000,
      // 5 seconds
      onInterest,
      onNegativeFeedback,
      viewThreshold = 15000 // 15 seconds
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Ref to the wrapper element
  const elementRef = useRef(null);
  // Track if component is visible
  const isVisibleRef = useRef(false);
  // Track if user is hovering
  const isHoveringRef = useRef(false);
  // Store timer references for cleanup
  const viewTimerRef = useRef(null);
  const hoverTimerRef = useRef(null);
  // Track if callbacks were already fired
  const interestShownRef = useRef(false);
  const feedbackSentRef = useRef(false);
  // Track start times for resuming timers
  const viewStartTimeRef = useRef(null);
  const hoverStartTimeRef = useRef(null);
  // Track accumulated hover time (view time does NOT accumulate)
  const accumulatedHoverTimeRef = useRef(0);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current);
      viewTimerRef.current = null;
    }
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  // Check if interest threshold is met and fire callback
  const checkInterestThreshold = useCallback(() => {
    if (!interestShownRef.current) {
      const viewTimeElapsed = viewStartTimeRef.current ? Date.now() - viewStartTimeRef.current : 0;
      if (viewTimeElapsed >= viewThreshold || accumulatedHoverTimeRef.current >= hoverThreshold) {
        interestShownRef.current = true;
        onInterest === null || onInterest === void 0 || onInterest();
      }
    }
  }, [viewThreshold, hoverThreshold, onInterest]);

  // Send negative feedback if interest was shown but no interaction occurred
  const checkAndSendFeedback = useCallback(() => {
    if (!feedbackSentRef.current && !bypassTrackingRef.current && interestShownRef.current) {
      feedbackSentRef.current = true;
      onNegativeFeedback === null || onNegativeFeedback === void 0 || onNegativeFeedback();
    }
  }, [bypassTrackingRef, onNegativeFeedback]);

  // Start view timer
  const startViewTimer = useCallback(() => {
    if (interestShownRef.current) return;
    viewStartTimeRef.current = Date.now();
    viewTimerRef.current = setTimeout(() => {
      checkInterestThreshold();
    }, viewThreshold);
  }, [viewThreshold, checkInterestThreshold]);

  // Start hover timer
  const startHoverTimer = useCallback(() => {
    if (interestShownRef.current) return;
    hoverStartTimeRef.current = Date.now();
    const remainingTime = hoverThreshold - accumulatedHoverTimeRef.current;
    hoverTimerRef.current = setTimeout(() => {
      accumulatedHoverTimeRef.current = hoverThreshold;
      checkInterestThreshold();
    }, remainingTime);
  }, [hoverThreshold, checkInterestThreshold]);

  // Reset timers (called on scroll)
  const resetTimers = useCallback(() => {
    clearAllTimers();
    // Reset view time (does NOT accumulate)
    viewStartTimeRef.current = null;
    // Reset hover time
    accumulatedHoverTimeRef.current = 0;
    hoverStartTimeRef.current = null;
    // Don't reset interestShownRef - once interest is shown, it stays shown

    // Restart view timer if component is still visible and interest not yet shown
    if (isVisibleRef.current && !interestShownRef.current) {
      startViewTimer();
    }
    // Restart hover timer if user is still hovering and interest not yet shown
    if (isHoveringRef.current && !interestShownRef.current) {
      startHoverTimer();
    }
  }, [clearAllTimers, startViewTimer, startHoverTimer]);

  // Handle mouse enter
  const handleMouseEnter = useCallback(() => {
    if (interestShownRef.current) return;
    isHoveringRef.current = true;
    startHoverTimer();
  }, [startHoverTimer]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;

    // Accumulate hover time
    if (hoverStartTimeRef.current) {
      accumulatedHoverTimeRef.current += Date.now() - hoverStartTimeRef.current;
      hoverStartTimeRef.current = null;
    }
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  // Setup intersection observer to track visibility
  useEffect(() => {
    if (interestShownRef.current) return;
    const element = elementRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      const wasVisible = isVisibleRef.current;
      const isNowVisible = entry.isIntersecting;

      // Only act if visibility state actually changed
      if (isNowVisible === wasVisible) return;
      isVisibleRef.current = isNowVisible;
      if (isNowVisible) {
        // Component became visible - start fresh timer
        startViewTimer();
      } else {
        // Component became hidden - reset view time (does NOT accumulate)
        viewStartTimeRef.current = null;
        if (viewTimerRef.current) {
          clearTimeout(viewTimerRef.current);
          viewTimerRef.current = null;
        }
      }
    }, {
      threshold: VISIBILITY_THRESHOLD
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [startViewTimer]);

  // Setup scroll listener to reset timers
  useEffect(() => {
    if (interestShownRef.current) return;
    const handleScroll = () => {
      resetTimers();
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [resetTimers]);

  // Send feedback if interest was shown
  useEffect(() => {
    return () => {
      // Finalize accumulated hover time
      if (hoverStartTimeRef.current) {
        accumulatedHoverTimeRef.current += Date.now() - hoverStartTimeRef.current;
      }
      checkAndSendFeedback();
    };
  }, [checkAndSendFeedback]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);
  return /*#__PURE__*/_jsx(View, Object.assign({
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    ref: elementRef
  }, props, {
    children: children
  }));
}